import { useCallback, useEffect, useRef, useState } from "react";
import { bringIntoView } from "../ui/scroll";
import { AppIcon } from "./AppIcon";
import { ApiError, type ApiClient } from "../api/client";
import type { QrPayment } from "../api/types";
import { t } from "../i18n";
import { formatEuro, type Cents } from "../lib/money";
import { QrCode } from "../ui/QrCode";
import { ASK_EVERY_MS, isOver, timeLeft } from "./bank-qr";

interface Props {
  api: ApiClient;
  /** What the customer pays in all, the tip included. */
  amountCents: Cents;
  tipCents: Cents;
  /** The table or the delivery; null for a takeaway that is made once it is paid. */
  orderId: string | null;
  /** Above the QR: "Scan met je bank-app". */
  scanText: string;
  /** Bancontact confirmed the payment: the screen around it books it (and prints, delivers…). */
  onPaid: (qr: QrPayment) => void;
  /** The screen around it is booking the payment. */
  booking: boolean;
  /** Booking failed for good: the money is at Bancontact, the order does not know it. */
  notBooked: boolean;
  /** Away from the QR without a payment: another method after all. */
  onLeave: () => void;
  /** The words on the way out: "Andere betaalwijze", "toch contant". */
  leaveText: string;
}

/**
 * The bank-QR of Bancontact Pro (ADR 0024): asks the server for a QR with the amount, shows it and asks
 * every two seconds how far it is. Only when Bancontact says it is paid does `onPaid` run, once. Leaving
 * withdraws the QR first; a customer who confirmed in that very second has paid, and then `onPaid` runs
 * instead of `onLeave`. While the QR is shown the screen around it offers no other way out.
 */
export function BankQrPanel({
  api,
  amountCents,
  tipCents,
  orderId,
  scanText,
  onPaid,
  booking,
  notBooked,
  onLeave,
  leaveText,
}: Props) {
  const text = t.tafoApp.bankQr;
  const [qr, setQr] = useState<QrPayment | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  /** Which QR was handed to the screen around it: never the same payment twice. */
  const handed = useRef<string | null>(null);
  /** Counts the QRs asked: an answer for an older one is dropped. */
  const round = useRef(0);

  // the screen around it gives a new function with every drawing: that must never ask a new QR
  const paid = useRef(onPaid);
  useEffect(() => {
    paid.current = onPaid;
  }, [onPaid]);

  const take = useCallback((next: QrPayment) => {
    setQr(next);
    if (next.status === "succeeded" && !next.booked && handed.current !== next.id) {
      handed.current = next.id;
      paid.current(next);
    }
  }, []);

  /** Asks the server for the QR; `mine` is its turn, so an answer that comes after a newer question is dropped. */
  const ask = useCallback(
    (mine: number) => {
      api
        .request<QrPayment>("POST", "/device/qr-payments", {
          body: { amountCents, ...(tipCents > 0 && { tipCents }), ...(orderId !== null && { orderId }) },
        })
        .then((made) => {
          if (round.current !== mine) return;
          setProblem(null);
          take(made);
        })
        .catch((error: unknown) => {
          if (round.current !== mine) return;
          setProblem(error instanceof ApiError ? error.message : t.errors.network);
        });
    },
    [api, amountCents, tipCents, orderId, take],
  );

  // one QR per amount: asked when the panel opens, and again when the amount changes. Asked twice for
  // the same thing (a screen drawn twice), the server gives the same QR.
  useEffect(() => {
    ask(++round.current);
    return () => {
      round.current += 1;
    };
  }, [ask]);

  /** "Nieuwe QR" or "Opnieuw proberen". */
  const start = () => {
    setQr(null);
    setProblem(null);
    ask(++round.current);
  };

  const running = qr !== null && (qr.status === "pending" || qr.status === "identified");
  const id = qr?.id ?? null;

  // on a phone the QR stands below the amounts and the methods: bring it up, with what is under it
  const box = useRef<HTMLDivElement>(null);
  const behind = qr?.upToDate === false;
  useEffect(() => {
    if (id !== null && box.current) {
      bringIntoView(box.current, { block: "end", smooth: true });
    }
  }, [id, behind]);
  useEffect(() => {
    if (!running || id === null || leaving) return;
    const mine = round.current;
    const ask = setInterval(() => {
      api
        .request<QrPayment>("GET", `/device/qr-payments/${id}`)
        .then((answer) => {
          if (round.current === mine) take(answer);
        })
        .catch(() => {
          // no connection for a moment: the next question follows in two seconds
          if (round.current === mine) setQr((current) => (current === null ? null : { ...current, upToDate: false }));
        });
    }, ASK_EVERY_MS);
    const clock = setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      clearInterval(ask);
      clearInterval(clock);
    };
  }, [api, id, running, leaving, take]);

  const leave = async () => {
    if (qr === null || !running) {
      onLeave();
      return;
    }
    setLeaving(true);
    try {
      const ended = await api.request<QrPayment>("DELETE", `/device/qr-payments/${qr.id}`);
      // confirmed in the very second the waiter went back: it is paid
      if (ended.status === "succeeded") {
        setLeaving(false);
        take(ended);
        return;
      }
    } catch {
      // the server keeps asking Bancontact; a payment that still came through shows in Meldingen
    }
    onLeave();
  };

  const out = (
    <button type="button" className="linkbtn" disabled={leaving || booking} onClick={() => void leave()}>
      {leaveText}
    </button>
  );

  if (problem !== null) {
    return (
      <>
        <p className="ph-note bad c" role="alert">
          {problem}
        </p>
        <button type="button" className="btn ph-big" onClick={start}>
          {text.retry}
        </button>
        {out}
      </>
    );
  }
  if (qr === null) return <p className="proto ob-hint c">{text.making}</p>;

  if (qr.status === "succeeded") {
    return (
      <>
        <p className="ob-qrhead ob-qrpaid" role="status">
          <b className="num">
            <AppIcon name="check" />
            {text.paid(formatEuro(qr.amountCents))}
          </b>
        </p>
        {notBooked ? (
          <>
            <p className="ph-note bad c" role="alert">
              {text.notBooked}
            </p>
            <button type="button" className="linkbtn" onClick={onLeave}>
              {t.tafoApp.waiter.back}
            </button>
          </>
        ) : (
          <p className="proto ob-hint c">{text.booking}</p>
        )}
      </>
    );
  }

  if (isOver(qr.status)) {
    return (
      <>
        <p className="ph-note bad c" role="alert">
          {text[qr.status as "failed" | "expired" | "cancelled"]}
        </p>
        <button type="button" className="btn ph-big" onClick={start}>
          {text.again}
        </button>
        {out}
      </>
    );
  }

  return (
    <div className="ob-qrbox" ref={box}>
      <p className="ob-qrhead">
        {scanText}
        <br />
        <b className="num">{formatEuro(qr.amountCents)}</b>
      </p>
      <div className="ph-qr">
        <QrCode text={qr.qrText} label={scanText} />
      </div>
      <p className="proto ob-hint c" role="status">
        {qr.status === "identified" ? text.scanned : text.waiting}
      </p>
      {/* apart from the status: a screen reader is not told every second */}
      <p className="proto ob-hint c num">{text.left(timeLeft(qr.expiresAt, now))}</p>
      {!qr.upToDate && (
        <p className="ph-note bad c" role="alert">
          {text.behind}
        </p>
      )}
      {out}
    </div>
  );
}
